import { gql } from "@apollo/client";
import FormSection from "controls/FormSection";
import Message from "controls/Message";
import MultiValuesField from "controls/MultiValuesField";
import toCapitalizedWords from "helpers/toCapitalizedWords";
import useData from "hooks/useData";
import useIsAdmin from "hooks/useIsAdmin";
import DurationInput from "inputs/DurationInput";
import SelectInput from "inputs/SelectInput";
import SwitchInput from "inputs/SwitchInput";
import TextInput from "inputs/TextInput";
import UploaderInput from "inputs/UploaderInput";
import React from "react";

export const OrgsFormFragment = gql`
  fragment OrgsFormFragment on Org {
    name
    active
    logoUploader
    primarySport
    haltGameNotifications
    disableGameNotifications
    countryCode
    timeZone
    usePublicLeaguePages
    maskPeopleEmail
    allowSignUp
    allowPracticeMode
    allowEncodeHighQualityOutputs
    disableNewGameVideos
    originalVideoRetentionPeriod
    presentDisclaimer
    disclaimer
    groupDisclaimer
    playlistsEnabled
    potwEnableForAdultLeagues
    potwEnableForChildLeagues
    potwEnableForTeenLeagues
    potwEnableForUnspecifiedAgeLeagues
    aiStatsEnableForAdultLeagues
    aiStatsEnableForChildLeagues
    aiStatsEnableForTeenLeagues
    aiStatsEnableForUnspecifiedAgeLeagues
    videoRecordingPermittedForAdultLeagues
    videoRecordingPermittedForChildLeagues
    videoRecordingPermittedForTeenLeagues
    videoRecordingPermittedForUnspecifiedAgeLeagues
    defaultGameDurationSeconds
  }
`;

export default function OrgsForm({ orgs = [{}] }) {
  const isAdmin = useIsAdmin();
  const [data] = useData(gql`
    query CitiesForm {
      timeZones
      glCountries {
        countryCode
        name
      }
      sports
    }
  `);
  // ensure fields default value align with database defaults and model defaults
  orgs = orgs.map((org) => ({
    active: true,
    primarySport: "basketball",
    allowSignUp: true,
    videoRecordingPermittedForAdultLeagues: true,
    videoRecordingPermittedForChildLeagues: true,
    videoRecordingPermittedForTeenLeagues: true,
    videoRecordingPermittedForUnspecifiedAgeLeagues: true,
    defaultGameDurationSeconds: 600,
    originalVideoRetentionPeriod: "infinite",
    ...org,
  }));

  const originalVideoRetentionPeriodOptions = [
    ["Infinite", "infinite"],
    ["180 days", "180"],
  ];

  return (
    <>
      <MultiValuesField
        name="name"
        label="Name"
        defaultValues={orgs.map((c) => c.name)}
        required
        input={<TextInput />}
      />
      <MultiValuesField
        name="active"
        label="Active"
        defaultValues={orgs.map((c) => c.active)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        name="logoUploader"
        label="Logo"
        defaultValues={orgs.map((c) => c.logoUploader)}
        input={<UploaderInput accept="image/*" />}
      />
      <MultiValuesField
        name="primarySport"
        label="Primary Sport"
        defaultValues={orgs.map((c) => c.primarySport)}
        required
        input={<SelectInput options={data?.sports.map((sport) => [toCapitalizedWords(sport), sport])} />}
      />
      <MultiValuesField
        name="defaultGameDurationSeconds"
        label="Default Game Duration"
        helperTextUiStringKey="readmin.org.fields.default_game_duration_seconds"
        defaultValues={orgs?.map((g) => g.defaultGameDurationSeconds)}
        input={<DurationInput unit="seconds" max={10800} />}
      />
      <MultiValuesField
        name="haltGameNotifications"
        label="Halt game notifications"
        helperTextUiStringKey="readmin.org.fields.halt_game_notifications"
        defaultValues={orgs.map((c) => c.haltGameNotifications)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        name="disableGameNotifications"
        label="Disable game notifications"
        helperTextUiStringKey="readmin.org.fields.disable_game_notifications"
        defaultValues={orgs.map((c) => c.disableGameNotifications)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        name="countryCode"
        label="Country"
        required
        defaultValues={orgs.map((city) => city.countryCode)}
        input={<SelectInput options={data?.glCountries.map(({ countryCode, name }) => [name, countryCode])} />}
      />
      <MultiValuesField
        name="timeZone"
        label="Time Zone"
        required
        defaultValues={orgs.map((city) => city.timeZone)}
        input={<SelectInput options={data?.timeZones} />}
      />
      <FormSection header="Plays of the Week" defaultExpanded={true}>
        <MultiValuesField
          name="potwEnableForAdultLeagues"
          label="Enable for Adult Leagues"
          defaultValues={orgs.map((c) => c.potwEnableForAdultLeagues)}
          input={<SwitchInput />}
        />
        <MultiValuesField
          name="potwEnableForChildLeagues"
          label="Enable for Child Leagues"
          defaultValues={orgs.map((c) => c.potwEnableForChildLeagues)}
          input={<SwitchInput />}
        />
        <MultiValuesField
          name="potwEnableForTeenLeagues"
          label="Enable for Teen Leagues"
          defaultValues={orgs.map((c) => c.potwEnableForTeenLeagues)}
          input={<SwitchInput />}
        />
        <MultiValuesField
          name="potwEnableForUnspecifiedAgeLeagues"
          label="Enable for Leagues with Unspecified Age Group"
          defaultValues={orgs.map((c) => c.potwEnableForUnspecifiedAgeLeagues)}
          input={<SwitchInput />}
        />
      </FormSection>
      {isAdmin && (
        <FormSection header="AI Stats" defaultExpanded={true}>
          <MultiValuesField
            name="aiStatsEnableForAdultLeagues"
            label="Enable for Adult Leagues"
            defaultValues={orgs.map((c) => c.aiStatsEnableForAdultLeagues)}
            input={<SwitchInput />}
          />
          <MultiValuesField
            name="aiStatsEnableForChildLeagues"
            label="Enable for Child Leagues"
            defaultValues={orgs.map((c) => c.aiStatsEnableForChildLeagues)}
            input={<SwitchInput />}
          />
          <MultiValuesField
            name="aiStatsEnableForTeenLeagues"
            label="Enable for Teen Leagues"
            defaultValues={orgs.map((c) => c.aiStatsEnableForTeenLeagues)}
            input={<SwitchInput />}
          />
          <MultiValuesField
            name="aiStatsEnableForUnspecifiedAgeLeagues"
            label="Enable for Leagues with Unspecified Age Group"
            defaultValues={orgs.map((c) => c.aiStatsEnableForUnspecifiedAgeLeagues)}
            input={<SwitchInput />}
          />
        </FormSection>
      )}
      <FormSection header="Video Recording" defaultExpanded={true}>
        <MultiValuesField
          name="videoRecordingPermittedForAdultLeagues"
          label="Permitted for Adult Leagues"
          defaultValues={orgs.map((c) => c.videoRecordingPermittedForAdultLeagues)}
          input={<SwitchInput />}
        />
        <MultiValuesField
          name="videoRecordingPermittedForChildLeagues"
          label="Permitted for Child Leagues"
          defaultValues={orgs.map((c) => c.videoRecordingPermittedForChildLeagues)}
          input={<SwitchInput />}
        />
        <MultiValuesField
          name="videoRecordingPermittedForTeenLeagues"
          label="Permitted for Teen Leagues"
          defaultValues={orgs.map((c) => c.videoRecordingPermittedForTeenLeagues)}
          input={<SwitchInput />}
        />
        <MultiValuesField
          name="videoRecordingPermittedForUnspecifiedAgeLeagues"
          label="Permitted for Leagues with Unspecified Age Group"
          defaultValues={orgs.map((c) => c.videoRecordingPermittedForUnspecifiedAgeLeagues)}
          input={<SwitchInput />}
        />
      </FormSection>
      <MultiValuesField
        name="usePublicLeaguePages"
        label="Use Public Leagues"
        helperTextUiStringKey="readmin.org.fields.use_public_league_pages"
        defaultValues={orgs.map((c) => c.usePublicLeaguePages)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        name="maskPeopleEmail"
        label="Mask People Email"
        helperTextUiStringKey="readmin.org.fields.mask_people_email"
        defaultValues={orgs.map((c) => c.maskPeopleEmail)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        name="allowSignUp"
        label="Allow Sign Up"
        helperTextUiStringKey="readmin.org.fields.allow_sign_up"
        defaultValues={orgs.map((c) => c.allowSignUp)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        name="allowPracticeMode"
        label="Allow Practice Mode"
        helperTextUiStringKey="readmin.org.fields.allow_practice_mode"
        defaultValues={orgs.map((c) => c.allowPracticeMode)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        name="allowEncodeHighQualityOutputs"
        label="Allow Encode High Quality Outputs"
        helperTextUiStringKey="readmin.org.fields.allow_encode_high_quality_outputs"
        defaultValues={orgs.map((c) => c.allowEncodeHighQualityOutputs)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        name="playlistsEnabled"
        label="Enable playlists"
        helperTextUiStringKey="readmin.org.fields.playlists_enabled"
        defaultValues={orgs.map((c) => c.playlistsEnabled)}
        input={<SwitchInput />}
      />
      <MultiValuesField
        name="disableNewGameVideos"
        label="Disable New Game Videos"
        helperTextUiStringKey="readmin.org.fields.disable_new_game_videos"
        defaultValues={orgs.map((c) => c.disableNewGameVideos)}
        input={<SwitchInput />}
      />
      <FormSection header="Scorekeeper Disclaimer">
        <MultiValuesField
          name="presentDisclaimer"
          label="Present Disclaimer"
          defaultValues={orgs.map((c) => c.presentDisclaimer)}
          input={<SwitchInput />}
        />
        <MultiValuesField
          name="disclaimer"
          label="Disclaimer"
          defaultValues={orgs.map((c) => c.disclaimer)}
          input={<TextInput multiline minRows={5} />}
        />
        <MultiValuesField
          name="groupDisclaimer"
          label="Group Disclaimer"
          defaultValues={orgs.map((c) => c.groupDisclaimer)}
          input={<TextInput multiline minRows={5} />}
        />
      </FormSection>
      <FormSection header="Retention Period">
        <div style={{ marginBottom: "1rem" }}>
          <Message
            type="warning"
            content={
              <>
                Changing retention periods settings will potentially result in videos being deleted or archived sooner
                than expected.
              </>
            }
          />
        </div>
        <MultiValuesField
          name="originalVideoRetentionPeriod"
          label="Original Video Retention Period"
          helperTextUiStringKey="readmin.org.fields.original_video_retention_period"
          defaultValues={orgs.map((c) => c.originalVideoRetentionPeriod)}
          required
          input={<SelectInput options={originalVideoRetentionPeriodOptions} />}
        />
      </FormSection>
    </>
  );
}
